import React, { FC, useCallback, useContext } from 'react';
import { TypographyBody1 } from '../Typography/Typography';
import { PasswordInputController } from '../InputControllers/PasswordInputController';
import Box from '@mui/system/Box';
import Button from '@mui/material/Button';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { apiAuth } from '../../resources/api/apiAuth';
import { NotificationContext } from '../../contexts/NotificationContext';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { WarningAlert } from '../Alert/WarningAlert';
import { useAuth } from '../../hooks/useAuth';
import { useApiTranslation } from '../../hooks/useApiTranslation';

interface IChangePasswordFields {
  old_password: string;
  new_password: string;
}

const { useChangePassword } = apiAuth;

export const ChangePassword: FC = () => {
  const { t } = useTranslation();
  const { apiT } = useApiTranslation();

  const { addNotification } = useContext(NotificationContext);
  const handleError = useApiErrorHandler();

  const { execute: changePassword } = useChangePassword();

  const passwordForm = useForm<IChangePasswordFields>();

  const { logOut } = useAuth();

  const onSubmit: SubmitHandler<IChangePasswordFields> = useCallback(
    async (payload) => {
      try {
        const response = await changePassword(payload);

        addNotification({
          message: apiT(response.message) || t('security.password.successMessage'),
          severity: 'success',
        });
        passwordForm.reset();
        await logOut();
      } catch (e) {
        handleError(e);
      }
    },
    [t, changePassword]
  );

  return (
    <form onSubmit={passwordForm.handleSubmit(onSubmit)}>
      <TypographyBody1>{t('security.password.title')}</TypographyBody1>

      <PasswordInputController
        control={passwordForm.control}
        errors={passwordForm.formState.errors}
        label={t('security.password.oldPassword')}
        name="old_password"
        rules={{ required: true }}
        InputProps={{
          autoComplete: 'current-password',
        }}
      />

      <PasswordInputController
        control={passwordForm.control}
        errors={passwordForm.formState.errors}
        label={t('security.password.newPassword')}
        name="new_password"
        rules={{ required: true }}
      />

      <Box mt={2}>
        <WarningAlert>{t('security.password.warning')}</WarningAlert>
      </Box>

      <Box mt={2}>
        <Button variant="contained" type="submit">
          {t('security.password.submit')}
        </Button>
      </Box>
    </form>
  );
};
